import { RepositoryFactory } from '@/api/repositoryFactory';
import { IUser } from '@/models/user.model';
import { PersistentStore } from './main';
const UserRepository = RepositoryFactory.get('user');

import useAuthentication from '@/composables/user/useAuthentication';

interface UserStoreModel extends Object {
  user: IUser | null;
  authToken: string;
  virtual: boolean;
  loading: boolean;
}

class UserStore extends PersistentStore<UserStoreModel> {
  protected data(): UserStoreModel {
    return {
      user: null,
      authToken: '',
      virtual: false,
      loading: false,
    };
  }
  private setUser(user: IUser) {
    this.state.user = user;
    if (user.virtual) {
      this.state.virtual = true;
    } else {
      this.state.virtual = false;
    }
  }
  private unsetUser() {
    this.state.user = null;
    this.state.virtual = false;
  }
  isLoading() {
    return this.state.loading && this.state.authToken;
  }
  isLoggedIn() {
    return (
      this.state.user &&
      (this.state.authToken !== '' || process.env.VUE_APP_INTERNAL_COOKIE_USE) &&
      !this.state.user.virtual &&
      !this.state.virtual
    );
  }
  isVirtualUser() {
    return this.state.virtual;
  }
  isAdmin() {
    if (this.state.user == null) return false;
    return this.state.user.type === 'admin';
  }
  setVirtualAuthToken(authToken: string) {
    if (!process.env.VUE_APP_ENABLE_VIRTUAL_USER) {
      return;
    }
    if (this.state.authToken) {
      // never overwrite authToken in store with virtual user
      console.error('auth token already set');
      return;
    }
    this.state.authToken = authToken;
    this.state.virtual = true;
    // try to fetch User right away after setting authToken
    this.fetchUser(true, true);
  }
  setAuthToken(authToken: string) {
    // validation in case we overwrite a user here needs to happen outside, befor calling the method!
    this.state.authToken = authToken;
    // try to fetch User right away after setting authToken
    this.fetchUser(false);
  }
  unsetAuthToken() {
    this.state.authToken = '';
    // always unset user, when authToken is unset
    this.unsetUser();
  }
  fetchUser(forceFetch: boolean, virtual = false) {
    this.state.loading = true;
    // if user was already fetched, simply return user object from state
    if (!forceFetch && this.state.user !== null) {
      this.state.loading = false;
    }

    // if authToken got set, fetch user and then return
    // else if we use widgets internally (included in wdw-server) try to fetch user (validation via server-cookie)
    if (this.state.authToken || process.env.VUE_APP_INTERNAL_COOKIE_USE) {
      let params = {};
      if (process.env.VUE_APP_INTERNAL_COOKIE_USE) {
        // always get virtual user from server if internal use is active
        params = { force_current_user: true };
      }
      UserRepository.getSelf(params)
        .then((response: any) => {
          if (response.data.success) {
            this.setUser(response.data.data);
          } else {
            console.log('error');
            this.forceLogoutOnFetchError(virtual);
          }
          this.state.loading = false;
        })
        .catch((error: any) => {
          console.log(error);
          this.forceLogoutOnFetchError(virtual);
          this.state.loading = false;
        });
    } else {
      // if we authToken is not set, unset User no matter what
      this.unsetUser();
      this.state.loading = false;
    }
  }
  forceLogoutOnFetchError(virtual = false) {
    const { setAuthToken, setVirtualAuthToken } = useAuthentication();
    // removes the token from storage & sends logout event to other comps
    if (virtual) {
      setVirtualAuthToken('');
    } else {
      setAuthToken('');
    }
    console.log('force logout user after auth_token cause errors on fetch');
  }
  hasAnyUserRole(roles: string[]) {
    if (this.state.user) {
      let result;
      for (let i = 0; i < roles.length; i++) {
        result = this.state.user?.user_roles.some((user_role: any) => {
          return user_role.name == roles[i];
        });
        if (result) {
          return result;
        }
      }
      return false;
    } else return false;
  }
}
export const userStore = new UserStore('USER_STORE');
